import React, { useContext } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useCookies } from 'react-cookie';
import CheckoutPageContext from '../../../context/CheckoutPageContext';

function OrderContactOverview() {
  const { control, getValues } = useFormContext();
  const { checkoutPageState } = useContext(CheckoutPageContext) || {};
  const { orderContact, textFor } = checkoutPageState || {};

  const [cookies] = useCookies(['subscribe']);

  // For stepped checkout, email and phone are stored at root level
  // For legacy checkout, fall back to address-specific paths
  const [email, phone, smsOptIn] = useWatch({
    control,
    name: ['email', 'phone', 'smsOptIn'],
  });

  // Get current form values to check address paths for legacy checkout
  const formValues = getValues();
  const addressType = orderContact?.addressType || 'homeDelivery';

  // Use current form values only - don't fall back to old context values
  const displayEmail = email || formValues?.addresses?.[addressType]?.email;
  const displayPhone = phone || formValues?.addresses?.[addressType]?.phone;

  const renderSmsOptIn = () => {
    if (smsOptIn) {
      const smsText = textFor?.smsOptIn?.value || 'Opted in for SMS notifications';
      return <p key="sms" className="overview-sms">{smsText}</p>;
    }
    return null;
  };

  const renderMarketingPreferences = () => {
    // Email marketing preferences from subscribe cookie
    if (!cookies.subscribe) {
      return null;
    }

    // Get brand information from marketing preferences data in context
    const marketingPreferences = checkoutPageState?.marketingPreferences;
    const brands = marketingPreferences?.brands || [];

    // Create a brand mapping from the actual marketing preferences data
    const brandsMap = brands.reduce((map, brand) => ({
      ...map,
      [brand.id]: brand.label?.value || brand.id,
    }), {});

    const optedInBrands = [];

    Object.entries(cookies.subscribe).forEach(([brandId, value]) => {
      if (value === 'Y') {
        // Use the actual brand labels from marketing preferences data
        const brandName = brandsMap[brandId] || brandId;
        optedInBrands.push(brandName);
      }
    });

    if (optedInBrands.length === 0) {
      return null;
    }

    let brandText;
    if (optedInBrands.length === 1) {
      [brandText] = optedInBrands;
    } else if (optedInBrands.length === 2) {
      brandText = `${optedInBrands[0]} ${textFor?.and?.value || 'and'} ${optedInBrands[1]}`;
    } else {
      brandText = `${optedInBrands.slice(0, -1).join(', ')}, ${textFor?.and?.value || 'and'} ${optedInBrands[optedInBrands.length - 1]}`;
    }

    const emailText = textFor?.marketingEmailOptIn?.value || 'Opted in for {brands} emails';
    const displayText = emailText.replace('{brands}', brandText);

    return <p key="email" className="overview-marketing">{displayText}</p>;
  };

  return (
    <div className="order-contact-overview" data-testid="order-contact-overview">
      <p className="overview-email" data-testid="order-contact-overview-email">{displayEmail}</p>
      <p className="overview-phone" data-testid="order-contact-overview-phone">{displayPhone}</p>
      {renderSmsOptIn()}
      {renderMarketingPreferences()}
    </div>
  );
}

export default OrderContactOverview;
